/**
 * USB Port Fields - USB inputs and outputs configuration
 */

import React from 'react';
import { Box, Flex, Text, TextField, Select, Checkbox, Button, IconButton } from '@radix-ui/themes';
import { PlusIcon, Cross2Icon } from '@radix-ui/react-icons';
import { DeviceTemplate } from '../../types';

interface USBPortFieldsProps {
  template: Partial<DeviceTemplate>;
  onChange: (updated: Partial<DeviceTemplate>) => void;
}

const USBPortFields: React.FC<USBPortFieldsProps> = ({ template, onChange }) => {
  const usbInputs = template.usbInputs || [];
  const usbOutputs = template.usbOutputs || [];

  return (
    <Box>
      {/* USB Inputs */}
      <Box>
        <Flex align="center" gap="2" mb="2">
          <Checkbox
            checked={usbInputs.length > 0}
            onCheckedChange={(checked) => {
              if (checked) {
                onChange({ ...template, usbInputs: [{ count: 1, version: 'USB 2.0', powered: false }] });
              } else {
                onChange({ ...template, usbInputs: [] });
              }
            }}
          />
          <Text size="1" weight="bold">
            Has USB Inputs (needs USB ports)
          </Text>
        </Flex>

        {usbInputs.length > 0 && (
          <Box p="3" style={{ backgroundColor: 'var(--gray-2)', borderRadius: 'var(--radius-2)' }} mb="3">
            <Flex justify="between" align="center" mb="2">
              <Text size="1" weight="bold">USB Inputs</Text>
              <Button
                type="button"
                size="1"
                variant="soft"
                onClick={() => onChange({
                  ...template,
                  usbInputs: [...usbInputs, { count: 1, version: 'USB 2.0', powered: false }]
                })}
              >
                <PlusIcon /> Add Input Type
              </Button>
            </Flex>
            
            <Flex direction="column" gap="2">
              {usbInputs.map((usb, index) => (
                <Flex key={index} gap="2" align="end">
                  <Box style={{ flex: 0.8 }}>
                    <Text as="label" size="1">Count</Text>
                    <TextField.Root
                      type="number"
                      min="1"
                      value={usb.count}
                      onChange={(e) => {
                        const updated = [...usbInputs];
                        updated[index].count = parseInt(e.target.value) || 1;
                        onChange({ ...template, usbInputs: updated });
                      }}
                      mt="1"
                    />
                  </Box>
                  <Box style={{ flex: 1.2 }}>
                    <Text as="label" size="1">USB Version</Text>
                    <Select.Root 
                      value={usb.version} 
                      onValueChange={(value) => {
                        const updated = [...usbInputs];
                        updated[index].version = value as any;
                        onChange({ ...template, usbInputs: updated });
                      }}
                    >
                      <Select.Trigger style={{ width: '100%' }} mt="1" />
                      <Select.Content>
                        <Select.Item value="USB 2.0">USB 2.0</Select.Item>
                        <Select.Item value="USB 3.0">USB 3.0</Select.Item>
                        <Select.Item value="USB 3.1">USB 3.1</Select.Item>
                        <Select.Item value="USB-C">USB-C</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Flex align="end" gap="2" style={{ paddingBottom: '8px' }}>
                      <Checkbox
                        checked={usb.powered}
                        onCheckedChange={(checked) => {
                          const updated = [...usbInputs];
                          updated[index].powered = checked === true;
                          onChange({ ...template, usbInputs: updated });
                        }}
                      />
                      <Text size="1">Powered</Text>
                    </Flex>
                  </Box>
                  {usbInputs.length > 1 && (
                    <IconButton
                      type="button"
                      size="1"
                      variant="soft"
                      color="red"
                      onClick={() => {
                        const updated = usbInputs.filter((_, i) => i !== index);
                        onChange({ ...template, usbInputs: updated });
                      }}
                    >
                      <Cross2Icon />
                    </IconButton>
                  )}
                </Flex>
              ))}
            </Flex>
          </Box>
        )}
      </Box>

      {/* USB Outputs */}
      <Box>
        <Flex align="center" gap="2" mb="2">
          <Checkbox
            checked={usbOutputs.length > 0}
            onCheckedChange={(checked) => {
              if (checked) {
                onChange({ ...template, usbOutputs: [{ count: 1, version: 'USB 2.0', powered: false }] });
              } else {
                onChange({ ...template, usbOutputs: [] });
              }
            }}
          />
          <Text size="1" weight="bold">
            Has USB Outputs (provides ports to devices)
          </Text>
        </Flex>

        {usbOutputs.length > 0 && (
          <Box p="3" style={{ backgroundColor: 'var(--gray-2)', borderRadius: 'var(--radius-2)' }}>
            <Flex justify="between" align="center" mb="2">
              <Text size="1" weight="bold">USB Outputs</Text>
              <Button
                type="button"
                size="1"
                variant="soft"
                onClick={() => onChange({
                  ...template,
                  usbOutputs: [...usbOutputs, { count: 1, version: 'USB 2.0', powered: false }]
                })}
              >
                <PlusIcon /> Add Output Type
              </Button>
            </Flex>
            
            <Flex direction="column" gap="2">
              {usbOutputs.map((usb, index) => (
                <Flex key={index} gap="2" align="end">
                  <Box style={{ flex: 0.8 }}>
                    <Text as="label" size="1">Count</Text>
                    <TextField.Root
                      type="number"
                      min="1"
                      value={usb.count}
                      onChange={(e) => {
                        const updated = [...usbOutputs];
                        updated[index].count = parseInt(e.target.value) || 1;
                        onChange({ ...template, usbOutputs: updated });
                      }}
                      mt="1"
                    />
                  </Box>
                  <Box style={{ flex: 1.2 }}>
                    <Text as="label" size="1">USB Version</Text>
                    <Select.Root 
                      value={usb.version} 
                      onValueChange={(value) => {
                        const updated = [...usbOutputs];
                        updated[index].version = value as any;
                        onChange({ ...template, usbOutputs: updated });
                      }}
                    >
                      <Select.Trigger style={{ width: '100%' }} mt="1" />
                      <Select.Content>
                        <Select.Item value="USB 2.0">USB 2.0</Select.Item>
                        <Select.Item value="USB 3.0">USB 3.0</Select.Item>
                        <Select.Item value="USB 3.1">USB 3.1</Select.Item>
                        <Select.Item value="USB-C">USB-C</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Flex align="end" gap="2" style={{ paddingBottom: '8px' }}>
                      <Checkbox
                        checked={usb.powered}
                        onCheckedChange={(checked) => {
                          const updated = [...usbOutputs];
                          updated[index].powered = checked === true;
                          onChange({ ...template, usbOutputs: updated });
                        }}
                      />
                      <Text size="1">Powered</Text>
                    </Flex>
                  </Box>
                  {usbOutputs.length > 1 && (
                    <IconButton
                      type="button"
                      size="1"
                      variant="soft"
                      color="red"
                      onClick={() => {
                        const updated = usbOutputs.filter((_, i) => i !== index);
                        onChange({ ...template, usbOutputs: updated });
                      }}
                    >
                      <Cross2Icon />
                    </IconButton>
                  )}
                </Flex>
              ))}
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default USBPortFields;
